import { useState, useRef, useEffect } from "react";
import api, { API_BASE_URL } from "../api";
import { useAuth } from "../context/AuthProvider";
import { useNavigate, NavLink } from "react-router-dom";
import InputText from '../components/Form/Fields/InputText';
import InputPassword from '../components/Form/Fields/InputPassword';
import Form from '../components/Form/Form';
import LoadingBar from 'react-top-loading-bar';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faArrowRight,
  faArrowLeft,
  faAngleRight,
  faCircleInfo,
  faShieldHalved,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import Checkbox from "../components/Form/Fields/Checkbox";
import { Tooltip } from 'react-tooltip';
import axios from "axios";
import { useTranslation, Trans } from 'react-i18next';
import LanguageSelector from "../components/LanguageSelector/LanguageSelector";
import GDPRConsent from "../components/Home/GDPRConsent";

import './Login.scss';

export default function Login() {
  const { t, i18n } = useTranslation();
  const isRtl = document.documentElement.dir === 'rtl';
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const ref = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Comptes Saydalani - Se connecter";
  }, []);

  // Shared by the plain login and the 2FA-verify step — once the backend has set the real
  // "jwt" session cookie, hydrate the AuthProvider's user/settings state and head in.
  const completeLogin = async () => {
    const result = await login();
    const defaultPage = result?.settings?.general_general_settings_default_page || '/dashboard';
    navigate(defaultPage, { replace: true });
    // login() sets the authenticated user, which unmounts this page right away
    // (PublicRoute redirects an authenticated visitor off /login) — ref may
    // already be gone by the time we get here.
    ref.current?.complete();
  };

  const handleLogin = async (data) => {
    ref.current.continuousStart();
    setIsLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/login`,
        { username: data.username, password: data.password, rememberMe: !!data.rememberMe },
        { withCredentials: true }
      );

      await completeLogin();
    } catch (err) {
      if (err.response?.data?.code === 'SUBSCRIPTION_INACTIVE') {
        navigate('/account-suspended', { state: { fromGate: true } });
      } else if (err.response?.data?.code === '2FA_REQUIRED') {
        setPendingToken(err.response.data.token);
        setTwoFactorStep(true);
        ref.current.complete();
      } else if (err.response) {
        toast.error(err.response.data.message || t('login.error.invalid_credentials'));
      } else if (err.request) {
        toast.error(t('login.error.server_down'));
      } else {
        toast.error(t('login.error.unexpected'));
      }
      setIsLoading(false);
      ref.current.complete();
    }
  };

  const handleVerifyCode = async (data) => {
    ref.current.continuousStart();
    setIsLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/login/2fa`,
        { pendingToken, code: data.code },
        { withCredentials: true }
      );

      await completeLogin();
    } catch (err) {
      if (err.response?.data?.code === 'RATE_LIMITED') {
        toast.error(err.response.data.message);
      } else if (err.response?.data?.message?.toLowerCase().includes('expir')) {
        toast.error(t('login.two_factor.error_expired'));
        setTwoFactorStep(false);
        setPendingToken(null);
      } else {
        toast.error(t('login.two_factor.error_invalid'));
      }
      setIsLoading(false);
      ref.current.complete();
    }
  };

  const backToCredentials = () => {
    setTwoFactorStep(false);
    setPendingToken(null);
  };

  return (
    <>
      <LoadingBar color="#007bff" ref={ref} height={3} shadow={true} />

      <div className="container-fluid min-vh-100 d-flex p-0 login-wrapper">

        <div className="d-flex flex-column justify-content-center align-items-center px-5 form-side">
          <div className="w-100" style={{ maxWidth: 450 }}>
            <div className="text-center mb-3">
              <NavLink to={`/${i18n.language}`} className="login-logo-link">
                <img className="mb-4 login-logo" src="/images/saydalani_logo.png" alt="Saydalani Logo" style={{ height: 35 }} />
              </NavLink>
              {twoFactorStep ? (
                <>
                  <h3 className="fw-bold mt-3 mb-3 welcome_back_title">
                    <FontAwesomeIcon icon={faShieldHalved} className="me-2" style={{ fontSize: '0.85em' }} />
                    {t('login.two_factor.title')}
                  </h3>
                  <div className="text-muted mb-5">
                    {t('login.two_factor.subtitle')}
                  </div>
                </>
              ) : (
                <>
                  <h3 className="fw-bold mt-3 mb-3 welcome_back_title">{t('login.title')}</h3>
                  <div className="text-muted mb-5">
                    {t('login.subtitle')}
                  </div>
                </>
              )}
            </div>

            {twoFactorStep ? (
              <Form id="twoFactorForm" onSubmit={handleVerifyCode}>
                {/* Decoys that absorb the browser's "autofill last saved login" heuristic —
                    right after a real login form, Chrome/Firefox will otherwise offer to fill
                    the saved email/password into the first text field they see, even though
                    this one is for a 6-digit code. Invisible, unregistered with react-hook-form,
                    never read on submit. */}
                <input type="text" name="username" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />
                <input type="password" name="password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />

                <div className="mb-3">
                  <div className="form-floating mb-4">
                    <InputText
                      id="code"
                      name="code"
                      disabled={isLoading}
                      validationRules={{ required: true }}
                      autofocus={true}
                      autoComplete="one-time-code"
                    />
                    <label htmlFor="code" className="form-label">{t('login.two_factor.code_label')}</label>
                  </div>
                </div>

                <button type="submit" className="small btn btn-dark btn-lg w-100 mt-3 rounded-pill px-5" style={{ fontSize: '19px', paddingBlock: 14 }} disabled={isLoading}>
                  {isLoading ? (
                    <>{t('login.two_factor.verifying')}</>
                  ) : (
                    <span className="fw-bold" style={{ fontSize: '17px' }}>{t('login.two_factor.submit')}</span>
                  )}
                  {isLoading
                    ? <span className="spinner-border spinner-border-sm" style={{ marginInlineStart: '1.5rem' }} />
                    : <FontAwesomeIcon icon={isRtl ? faArrowLeft : faArrowRight} style={{ marginInlineStart: '1.5rem' }} />}
                </button>

                <div className="text-center mt-3 p-3 login-help-link">
                  <button type="button" className="btn btn-link p-0" onClick={backToCredentials} disabled={isLoading}>
                    <FontAwesomeIcon className="me-2" icon={faArrowLeft} />
                    {t('login.two_factor.back')}
                  </button>
                </div>
              </Form>
            ) : (
              <Form id="loginForm" onSubmit={handleLogin}>
                <div className="mb-3">
                  <div className="form-floating mb-4">
                    <InputText
                      id="username"
                      name="username"
                      type="email"
                      disabled={isLoading}
                      validationRules={{ required: t('login.email_required') }}
                      autofocus={true}
                      autoComplete="username"
                    />
                    <label htmlFor="username" className="form-label">{t('login.email_label')}</label>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="mb-3">
                    <InputPassword
                      id="password"
                      name="password"
                      disabled={isLoading}
                      floating={true}
                      placeholder={t('login.password_placeholder')}
                      validationRules={{ required: t('login.password_required') }}
                      forgotPasswordTo="/forgot-password"
                      forgotPasswordLabel={t('login.forgot_password')}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <div className="mb-3 d-flex align-items-center gap-2 login-remember-row">
                    <Checkbox name={"rememberMe"} label={t('login.remember_me')} />
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      id="remember-me-info"
                      style={{ color: '#9ca3af', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <Tooltip
                      anchorSelect="#remember-me-info"
                      openEvents={{ click: true }}
                      closeEvents={{ click: true }}
                      place="top"
                      style={{ maxWidth: 260, fontSize: 12, lineHeight: 1.5, textAlign: 'left' }}
                    >
                      <Trans i18nKey="login.remember_me_tooltip" components={{ 1: <strong /> }} />
                    </Tooltip>
                  </div>
                </div>

                <button type="submit" className="small btn btn-dark btn-lg w-100 mt-3 rounded-pill px-5" style={{ fontSize: '19px', paddingBlock: 14 }} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      {t('login.signing_in')}
                    </>
                  ) : (
                    <>
                      <span className="fw-bold" style={{ fontSize: '18px' }}>{t('login.continue')}</span>
                    </>
                  )}
                  {isLoading
                    ? <span className="spinner-border spinner-border-sm" style={{ marginInlineStart: '1.5rem' }} />
                    : <FontAwesomeIcon icon={isRtl ? faArrowLeft : faArrowRight} style={{ marginInlineStart: '1.5rem' }} />}
                </button>
              </Form>
            )}

            {!twoFactorStep && (
              <div className="login-register-cta">
                <div className="login-register-cta__divider"><span>{t('login.no_account')}</span></div>
                <NavLink to={`/${i18n.language}/register`} className="login-register-cta__btn">
                  <FontAwesomeIcon icon={faUserPlus} />
                  {t('login.create_account')}
                </NavLink>
              </div>
            )}

            <div className="text-center text-white login_footer small">
              <div className="d-flex justify-content-center" style={{ marginBottom: 20 }}>
                <LanguageSelector className="login-lang-selector" />
              </div>
              <p className="small mb-2">
                {t('landing.footer.copyright', { year: new Date().getFullYear() })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <GDPRConsent />
    </>
  );
}
